import { guardArray } from "@msuite/utils/array";
import { motion } from "motion/react";
import { matchPath, useLocation, useNavigate } from "react-router";
import { useAppShellStore } from "../../app-shell";
import { Tooltip } from "../../tooltip";
import { cn } from "../../utils";
import type { NavigationItemType } from "../types";
import { SidebarNavigationGroup } from "./sidebar-navigation-group";

/** Props Interface */
interface SidebarNavigationItemProps {
	navigationItem: NavigationItemType;
}

export function SidebarNavigationItem({ navigationItem }: SidebarNavigationItemProps) {
	const subItems = navigationItem.items;
	const hasSubItems = guardArray(subItems);

	const { sidebarIsCollapsed } = useAppShellStore();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	/** Group */
	if (hasSubItems.length > 0) return <SidebarNavigationGroup navigationItem={navigationItem} />;
	if (!navigationItem?.to) return null;

	function isRouteActive(to: string) {
		return !!matchPath({ path: to, end: true }, pathname);
	}

	function handleOnNavigate() {
		if (!navigationItem.to) return;
		navigate(navigationItem.to);
	}

	const isActive = isRouteActive(navigationItem.to);

	const Icon = navigationItem.icon;

	const button = (
		<button
			type="button"
			className={cn(
				"w-full text-sm cursor-pointer relative active:opacity-80 py-2",
				sidebarIsCollapsed ? "px-2" : "px-4",
			)}
			onClick={handleOnNavigate}
		>
			<div className="ring-2 absolute inset-x-2 inset-y-0 rounded-base ring-accent-500/50 hidden" />
			{isActive && (
				<motion.div
					layoutId="sidebar-navigation-floater"
					className="absolute inset-x-2 inset-y-0 card z-0 shadow-none"
					transition={{
						duration: 0.2,
						ease: "circInOut",
					}}
				/>
			)}
			<div
				className={cn(
					"h-stack relative z-1 gap-3 overflow-hidden",
					sidebarIsCollapsed && "justify-center",
				)}
			>
				<motion.div
					animate={{
						scale: isActive ? 1 : 0.75,
					}}
				>
					<Icon
						color={isActive ? "var(--color-accent-500)" : "var(--color-primary-500)"}
						size={14}
						className="shrink-0"
					/>
				</motion.div>
				{!sidebarIsCollapsed && <p className="truncate">{navigationItem.label}</p>}
			</div>
		</button>
	);

	/** Guard */
	if (!sidebarIsCollapsed) return button;

	/** Render */
	return (
		<Tooltip
			label={navigationItem.label}
			placement="right"
		>
			{button}
		</Tooltip>
	);
}
